(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    define(['jquery',
            'underscore',
            'torso/modules/ListView',
            'torso/modules/NestedCell',
            './SecureMessageDataBehavior',
            'app/pagination/PaginationView',
            './SecureMessageDefaultView',
            'app/financial/financialFeatureService',
            'templates',
            './messageService',
            'app/applicationUtils'], factory);
  } else if (typeof exports === 'object') {
    // CommonJS-style exports
    var $ = require('jquery'),
        _ = require('underscore'),
        TorsoListView = require('backbone-torso/modules/ListView'),
        TorsoNestedCell = require('backbone-torso/modules/NestedCell'),
        SecureMessageDataBehavior = require('./SecureMessageDataBehavior'),
        financialFeatureService = require('./../financial/financialFeatureService'),
        TorsoPaginationView = require('./../pagination/PaginationView'),
        SecureMessageDefaultView = require('./SecureMessageDefaultView'),
        templates = {
          secureMessageWidgetTemplate: require('./secureMessageWidgetTemplate.hbs')
        },
        messageService = require('./messageService'),
        applicationUtils = require('./../applicationUtils');

    module.exports = factory($, _, TorsoListView, TorsoNestedCell, SecureMessageDataBehavior, financialFeatureService,
      TorsoPaginationView, SecureMessageDefaultView, templates, messageService, applicationUtils);
  } else {
    throw 'Only AMD and CommonJS imports are supported.';
  }
}(this, function($, _, TorsoListView, TorsoNestedCell, SecureMessageDataBehavior, financialFeatureService,
                  TorsoPaginationView, SecureMessageDefaultView, templates, messageService, applicationUtils) {
  'use strict';

  var withSidebarClassNameModifier = '';

  if (applicationUtils.currentApplication === applicationUtils.APPLICATIONS.ADMIN_TOOL) {
    withSidebarClassNameModifier = ' brec-tab__content--iframe brec-tab__content--side';
  }

  /**
   * This view renders a List View with pagination
   * @class TorsoSecureMessageWidget
   * @constructor
   * @extends Torso.ListView
   * @param options {Object}
   *   @param options.noDataMessageKey {String} i18n key to display when no messages are found
   *   @param options.loadingMessageKey {String} i18n key to display while the list is loading
   *   @param options.headerMessageKey {String} i18n key to display on the top of the view
   *   @param options.fetchFailedMessageKey {String} i18n key to display if the fetch fails
   *   @param options.hideIfEmpty {boolean} if true, hide the widget entirely if there are no results (defaults to false)
   */
  return TorsoListView.extend({
    tagName: 'section',
    className: 'brec-tab__content' + withSidebarClassNameModifier,
    id: 'tab-content',
    template: templates.secureMessageWidgetTemplate,
    itemView: SecureMessageDefaultView,
    itemContainer: 'secure-message-items',
    instructionsMessageKey: null,

    prepareFields: {
      headerMessageKey: 'headerMessageKey',
      noDataMessageKey: 'noDataMessageKey',
      loadingMessageKey: 'loadingMessageKey',
      fetchFailedMessageKey: 'fetchFailedMessageKey',
      instructionsMessageKey: 'instructionsMessageKey',
      showPaymentButton: '_checkPaymentAvailable',
      currentApplication: applicationUtils.currentApplication
    },

    /**
     * Events for buttons on the view
     * @property events {Object}
     *   @property events.'click [data-refresh-page-button]' {String}
     */
    events: {
      'click [data-refresh-page-button]': 'fetchContent'
    },

    criteria: {},

    /**
     * @method getNumberOfMessages
     * @returns {Number} the number of messages displayed by this widget.
     */
    getNumberOfMessages: function() {
      var modelsToRender = this.modelsToRender();
      return modelsToRender && modelsToRender.length || 0;
    },

    /**
     * @method getSelectedItemViews
     * @return {TorsoView[]} all of the views that are selected (as determined by isSelected() method on the view).
     *                       Any views that do not have an isSelected() method are considered not selected.
     */
    getSelectedItemViews: function() {
      return _.filter(this.getItemViews(), function(itemView) {
        return itemView && _.isFunction(itemView.isSelected) && itemView.isSelected();
      });
    },

    /**
     * @method isShowingResults
     * @returns {Boolean} true if this widget is showing results, false otherwise.
     */
    isShowingResults: function() {
      return this.get('showResults');
    },

    /**
     * @method isFetchSuccess
     * @return {Boolean} true if this view has already fetched and it was successful.  False otherwise.
     */
    isFetchSuccess: function() {
      return this.get('fetchSuccess');
    },

    /**
     * @method hasFetchedData
     * @return {Boolean} true if this view has already fetched and it was successful and there is data to display.  False otherwise.
     */
    hasFetchedData: function() {
      return this.get('fetchSuccess') && this.get('hasData');
    },

    /**
     * Re-fetches the data used by this view.
     * @method fetchContent
     */
    fetchContent: function() {
      this.fetchMessages();
    },

    /**
     * Set the criteria based on either what's stored in
     * initialCriteria (which comes from) initialize's options
     * or from the result of criteria (mostly coming from the
     * TodoListWidget). Set the page and patientID if those
     * are things that exist.
     */
    updateCriteria: function() {
      var criteria = this._initialCriteria || _.result(this, 'criteria');
      if (typeof this.get('page') !== 'undefined') {
        criteria.pagingAndSorting.pageNumber = this.get('page');
      }

      if (this.get('patientVecnaId')) {
        criteria.vecnaIds = [this.get('patientVecnaId')];
      }

      this.set('criteria', criteria);
    },

    behaviors: {
      secureMessage: {
        behavior: SecureMessageDataBehavior,
        renderOnFetch: true,
        alwaysFetch: true,
        updateEvents: 'viewState:change:criteria behaviors.secureMessage.data:usernameStatusUpdated',
        ids: function() {
          var secureMessageWidget = this.view;
          var secureMessageCriteria = secureMessageWidget.get('criteria');
          return messageService.fetchMessageIdsByCriteria(secureMessageCriteria)
            .then(function(response) {
              // This is a little weird, but this has to be tracked here - this is the only place that the original
              // response is available with paging info and the view is available to update.
              secureMessageWidget.set('fullListSize', response.fullListSize, { silent: true });
              // Setting a nested property w/o upsetting the rest of the criteria requires this to be separate from above.
              // Only update the paging and sorting of the view if the server endpoint supports returning it.
              if (response.pagingAndSorting) {
                secureMessageWidget.set('criteria.pagingAndSorting', response.pagingAndSorting, { silent: true });
              }
              return response.list;
            });
        }
      }
    },

    /**
     * @method initialize
     * @param options {Object}
     *   @param options.patientVecnaId {String} the vecna id of the patient to lookup messages for.
     *   @param options.criteria {Object} the base criteria to use for retrieving the collection of messages.
     *   @param options.noDataMessageKey {String} i18n key to display when no messages are found
     *   @param options.loadingMessageKey {String} i18n key to display while the list is loading
     *   @param options.headerMessageKey {String} i18n key to display on the top of the view
     *   @param options.fetchFailedMessageKey {String} i18n key to display if the fetch fails
     *   @param options.hideIfEmpty {boolean} if true, hide the widget entirely if there are no results (defaults to false).
     *   @param options.selectable {Boolean} true if each item should render a select checkbox, false otherwise.
     *   @param options.defaultSelected {Boolean} the initial value for the selection of each item.
     *   @param options.itemContext {Object} the context to pass to the item views.
     *     @param options.itemContext.selectable {Boolean} true if each item should render a select checkbox, false otherwise.
     *     @param options.itemContext.defaultSelected {Boolean} the initial value for the selection of each item.
     * @override
     */
    initialize: function(options) {
      options = options || {};
      var secureMessageDataBehavior = this.getBehavior('secureMessage');
      this.collection = secureMessageDataBehavior.data.privateCollection;

      options.itemContext = options.itemContext || {};
      options.itemContext.selectable = _.isUndefined(options.selectable) ? options.itemContext.selectable : options.selectable;
      options.itemContext.defaultSelected = _.isUndefined(options.defaultSelected) ? options.itemContext.defaultSelected : options.defaultSelected;

      this.set('patientVecnaId', options.patientVecnaId);
      this._initialCriteria = options.criteria;

      this.updateCriteria();

      if (options.patientVecnaId) {
        options.itemContext.patientVecnaId = options.patientVecnaId;
      }

      this.set('fullListSize', 0);

      _.extend(this, _.pick(options, ['headerMessageKey', 'noDataMessageKey', 'loadingMessageKey', 'fetchFailedMessageKey', 'hideIfEmpty']));

      this._paginationView = new TorsoPaginationView();

      _.bindAll(this, '_updateState', 'fetchContent', 'fetchMessages', 'updateCriteria');

      this.listenTo(this.viewState, 'change:patientVecnaId change:page change:criteria change:criteria.billCriteria', this.updateCriteria);
      this.listenTo(this.viewState, 'change:criteria.pagingAndSorting.pageNumber', this._updatePaginationView);
      this.listenTo(secureMessageDataBehavior, 'fetched', this._triggerMessagesFetched);
      this.listenTo(secureMessageDataBehavior, 'fetched', this._updateState);
      this.listenTo(this.viewState, 'change', this.render);
      this.on('child-view-added', this._listenToChildView);
      this.on('child-view-removed', this._stopListeningToChildView);
    },

    /**
     * @method prerender
     * @override
     */
    prerender: function() {
      var hasData = !!this.collection.length;
      this.set('hasData', hasData);
      this.set('showResults', hasData || !this.hideIfEmpty || !this.get('fetchSuccess'));
    },

    /**
     * @override
     * @method attachTrackedViews
     */
    attachTrackedViews: function() {
      if (this.get('fullListSize') > this.get('criteria.pagingAndSorting.pageSize')) {
        this.attachView('pagination', this._paginationView);
      }
    },

    /**
     * @method _dispose
     * @override
     */
    _dispose: function() {
      this.collection.dispose();
    },

    /**
     * This will set the pageNumber on the criteria in the ViewState
     * If the currentPage is NaN or undefined it will set it to 1
     * Assume that the incoming page number is based on the route which is 1-based (internally it is saved as zero-based).
     * @method  setPageNumber
     * @param {Number} page The currentPage based on the url
     */
    setPage: function(page) {
      var pageIndex;

      if (_.isString(page)) {
        page = parseInt(page, 10);
      }
      if (!_.isNumber(page)) {
        page = 1;
      }
      page = Math.max(1, page);

      pageIndex = page - 1;

      this.set('page', pageIndex);
    },

    /**
     * A method to fetch all the secureMessages needed by the page
     * @method fetchMessages
     */
    fetchMessages: function() {
      this.getBehavior('secureMessage').fetch();
    },

    /**
     * Called when the RequesterCollection's fetch is done to trigger an event on this view for other views to listen to.
     * @private
     * @method _triggerMessagesFetched
     */
    _triggerMessagesFetched: function() {
      this.trigger('messagesFetched', arguments);
    },

    /**
     * Called when the RequesterCollection's fetch is done.
     * It will change the state of the view's attributes and update the DOM
     * @param fetchResult {Object} The fetch result.
     *   @param fetchResult.status {String} 'success' then it was successful, 'failed' then it failed.
     * @private
     * @method _updateState
     */
    _updateState: function(fetchResult) {
      var fetchSuccess = (fetchResult.status === 'success');
      this.set('fetchSuccess', fetchSuccess);
      this._updatePaginationView();
    },

     /**
     * This function will update the PaginationView with information from the viewState
     * @private
     * @method updatePaginationView
     */
    _updatePaginationView: function() {
      var zeroBasedPageNumber = this.get('criteria.pagingAndSorting.pageNumber') || 0;
      var oneBasedPageNumber = zeroBasedPageNumber + 1;
      this._paginationView.updateState({
        fullListSize: this.get('fullListSize'),
        pageNumber: oneBasedPageNumber,
        pageSize: this.get('criteria.pagingAndSorting.pageSize')
      });
    },

    /**
     * Check payment allowed
     * @method _checkPaymentAllowed
     */
    _checkPaymentAllowed: function() {
      var patientPaymentAllowed = financialFeatureService.patientPaymentAllowed(this.patientVecnaId);
      var adminPaymentAllowed = financialFeatureService.adminPaymentAllowed();
      return patientPaymentAllowed || adminPaymentAllowed;
    },

    /**
     * @method _checkPaymentAvailable
     * @returns {boolean|*}
     * @private
     */
    _checkPaymentAvailable: function() {
      var onBillsDueTab = this.moduleNamespace === 'billsDueListWidget';
      var paymentAllowed = this._checkPaymentAllowed();
      return (onBillsDueTab && paymentAllowed);
    },

    /**
     * Listen to the child view for the selected property to change and trigger 'itemView:change:selected' on this parent class.
     * @method _listenToChildView
     * @param messageItemDetails {Object}
     *   @param messageItemDetails.view {Torso.Event} an object that emits the 'change:selected' event.
     * @private
     */
    _listenToChildView: function(messageItemDetails) {
      var messageItemView = messageItemDetails.view;
      this.listenTo(messageItemView, 'change:selected', function() {
        this.trigger('itemView:change:selected', messageItemView);
      });
    },

    /**
     * Stop listening to the child if it is disposed or removed from this list view.
     * @method _stopListeningToChildView
     * @param messageItemDetails {Object}
     *   @param messageItemDetails.view {Torso.Event} an object that emits the 'change:selected' event.
     * @private
     */
    _stopListeningToChildView: function(messageItemDetails) {
      var messageItemView = messageItemDetails.view;
      this.stopListening(messageItemView, 'change:selected');
    }
  });
}));
